import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select, { components } from 'react-select';
import Button from '@material-ui/core/Button';
import { apiList, verifyLogin } from '../../helpers';

// Branch + State filters with "Clear all", limited to what the signed-in
// user may see (options from /dashboard/filters). Used by Home and the loan
// map. `value` / `onChange`: { branches: [ifsc], states: [name] }.
//
// Each filter is a searchable checkbox list: the box shows a summary
// ("All branches", the name, or "3 branches"), the list stays open while
// ticking, the search text is kept between ticks, and "Select all" / "Clear"
// act on the options matching the search.

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 10,
    borderColor: state.isFocused ? '#4582FF' : '#D7DEEA',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(69, 130, 255, 0.2)' : 'none',
    cursor: 'pointer',
    '&:hover': { borderColor: '#B9C3D6' },
  }),
  // Summary ("3 branches") in ink; the "Search…" hint while open in grey.
  placeholder: (base, state) => ({
    ...base,
    color: state.hasValue && !state.selectProps.menuIsOpen ? '#1F2733' : '#8A94A6',
    fontWeight: state.hasValue && !state.selectProps.menuIsOpen ? 600 : 400,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 'calc(100% - 8px)',
  }),
  menu: (base) => ({
    ...base, minWidth: 260, borderRadius: 10, overflow: 'hidden',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 1400 }),
  option: (base, state) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 14,
    color: '#1F2733',
    background: state.isFocused ? '#F4F6FB' : '#fff',
    cursor: 'pointer',
  }),
};

function CheckboxOption(props) {
  const { isSelected, label } = props;
  return (
    <components.Option {...props}>
      <input type='checkbox' checked={isSelected} readOnly tabIndex={-1} style={{ margin: 0, pointerEvents: 'none' }} />
      <span>{label}</span>
    </components.Option>
  );
}

// "Select all" / "Clear" above the options (for the options matching the search).
function MenuWithTools(props) {
  const {
    children, options, getValue, setValue, selectProps,
  } = props;
  const search = (selectProps.inputValue || '').toLowerCase();
  const visible = options.filter((o) => !search || o.label.toLowerCase().includes(search));
  const selected = getValue();
  const keep = (e) => e.preventDefault(); // don't close / blur the menu
  const selectAll = () => {
    const values = new Set(selected.map((o) => o.value));
    setValue([...selected, ...visible.filter((o) => !values.has(o.value))], 'select-option');
  };
  const clear = () => {
    const drop = new Set(visible.map((o) => o.value));
    setValue(search ? selected.filter((o) => !drop.has(o.value)) : [], 'deselect-option');
  };
  return (
    <components.MenuList {...props}>
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', padding: '6px 12px 8px', fontSize: 12, color: '#5B6472', borderBottom: '1px solid #EEF1F6',
        }}
      >
        <span>{search ? `${visible.length} match · ${selected.length} selected` : `${selected.length} selected · type to search`}</span>
        <span style={{ display: 'flex', gap: 12 }}>
          <button type='button' onMouseDown={keep} onClick={selectAll} className='scope-tool' disabled={!visible.length}>
            {search ? 'Select matches' : 'Select all'}
          </button>
          <button type='button' onMouseDown={keep} onClick={clear} className='scope-tool' disabled={!selected.length}>
            Clear
          </button>
        </span>
      </div>
      {children}
    </components.MenuList>
  );
}

const SELECT_COMPONENTS = {
  Option: CheckboxOption,
  MenuList: MenuWithTools,
  IndicatorSeparator: null,
  MultiValue: () => null,
};

function CheckList({
  options, values, onChange, noun, plural, ariaLabel,
}) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const chosen = options.filter((o) => values.includes(o.value));
  let summary = `All ${plural}`;
  if (chosen.length === 1) summary = chosen[0].label;
  else if (chosen.length > 1) summary = `${chosen.length} ${plural}`;
  return (
    <Select
      // While the list is open the box is the search field.
      onMenuOpen={() => setOpen(true)}
      onMenuClose={() => setOpen(false)}
      isSearchable
      className='home-filter'
      isMulti
      options={options}
      value={chosen}
      onChange={(selected) => onChange((selected || []).map((o) => o.value))}
      placeholder={open ? `Search ${plural}…` : summary}
      controlShouldRenderValue={false}
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      isClearable={false}
      backspaceRemovesValue={false}
      inputValue={input}
      onInputChange={(value, meta) => {
        // Keep the search text while ticking; clear it when the menu closes.
        if (meta.action === 'input-change') setInput(value);
        if (meta.action === 'menu-close') setInput('');
      }}
      components={SELECT_COMPONENTS}
      styles={selectStyles}
      menuPortalTarget={document.body}
      noOptionsMessage={() => `No ${noun} matches`}
      aria-label={ariaLabel}
    />
  );
}

export default function ScopeFilters({ value, onChange, onOptions }) {
  const [options, setOptions] = useState({ branches: [], states: [] });
  const { branches, states } = value;

  useEffect(() => {
    const { session } = verifyLogin();
    axios.get(apiList.DASHBOARD_FILTERS, { headers: { session } })
      .then((r) => {
        const next = { branches: r.data.data.branches || [], states: r.data.data.states || [] };
        setOptions(next);
        if (onOptions) onOptions(next);
      })
      .catch(() => {});
  }, []);

  // Branch options follow the chosen states; names repeated across banks get their IFSC.
  const branchOptions = useMemo(() => {
    const list = options.branches.filter((b) => !states.length || states.includes(b.state));
    const counts = {};
    list.forEach((b) => { counts[b.branchName] = (counts[b.branchName] || 0) + 1; });
    return list.map((b) => ({
      value: b.ifscCode,
      label: counts[b.branchName] > 1 ? `${b.branchName} (${b.ifscCode})` : (b.branchName || b.ifscCode),
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [options.branches, states]);
  const stateOptions = options.states.map((s) => ({ value: s, label: s }));

  const changeStates = (next) => {
    let nextBranches = branches;
    if (next.length) {
      // Drop chosen branches that are outside the chosen states.
      const inStates = new Set(options.branches
        .filter((b) => next.includes(b.state)).map((b) => b.ifscCode));
      nextBranches = branches.filter((c) => inStates.has(c));
    }
    onChange({ branches: nextBranches, states: next });
  };

  const active = Boolean(branches.length || states.length);
  return (
    <div className='home-filters'>
      {/* Always rendered (hidden when nothing is chosen) and on the left, so
          the filter boxes never move when it appears. */}
      <Button
        color='primary'
        className={`scope-clear ${active ? '' : 'is-hidden'}`}
        aria-hidden={!active}
        tabIndex={active ? 0 : -1}
        onClick={() => onChange({ branches: [], states: [] })}
      >
        Clear all
      </Button>
      <CheckList
        options={branchOptions}
        values={branches}
        onChange={(next) => onChange({ branches: next, states })}
        noun='branch'
        plural='branches'
        ariaLabel='Filter by branch'
      />
      <CheckList
        options={stateOptions}
        values={states}
        onChange={changeStates}
        noun='state'
        plural='states'
        ariaLabel='Filter by state'
      />
    </div>
  );
}
